/**
 * Wheel palettes.
 *
 * Colours are CSS custom properties defined in `index.css` so a palette can be
 * re-themed without touching the component. Opacities live here because the
 * §3.1 states are distinguished as much by weight as by hue, and casino mode
 * (§8) needs a harder-contrast variant of the same eight states.
 */

import type { PocketVisualState } from './pocketState';

export interface StateTreatment {
  /** Overlay fill on top of the base pocket colour. Never opaque. */
  readonly overlay: string | null;
  readonly overlayOpacity: number;
  readonly stroke: string | null;
  readonly strokeWidth: number;
  readonly strokeDash: string | null;
  readonly labelColour: string;
}

export interface WheelPalette {
  readonly id: 'default' | 'casino';
  readonly pocketRed: string;
  readonly pocketBlack: string;
  readonly pocketGreen: string;
  readonly separator: string;
  readonly gapArc: string;
  readonly gainBar: string;
  readonly lossBar: string;
  readonly baselineRing: string;
  readonly hub: string;
  readonly chipBadge: string;
  readonly states: Readonly<Record<PocketVisualState, StateTreatment>>;
}

export const DEFAULT_WHEEL_PALETTE: WheelPalette = {
  id: 'default',
  pocketRed: 'var(--color-pocket-red)',
  pocketBlack: 'var(--color-pocket-black)',
  pocketGreen: 'var(--color-pocket-green)',
  separator: 'rgba(255,255,255,0.10)',
  gapArc: 'rgba(154,154,173,0.38)',
  gainBar: 'var(--color-gain)',
  lossBar: 'var(--color-loss)',
  baselineRing: 'rgba(255,255,255,0.13)',
  hub: 'var(--color-surface-1)',
  chipBadge: 'rgba(255,255,255,0.82)',
  states: {
    // 1 — warm amber fill, dashed white outline.
    'hover-remove': {
      overlay: 'var(--color-remove)',
      overlayOpacity: 0.62,
      stroke: '#ffffff',
      strokeWidth: 1.8,
      strokeDash: '3 2.4',
      labelColour: '#ffffff',
    },
    // 2 — cyan wash, solid cyan outline.
    'hover-add-new': {
      overlay: 'var(--color-preview)',
      overlayOpacity: 0.5,
      stroke: 'var(--color-preview-strong)',
      strokeWidth: 1.8,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    // 3 — outline only; the placed fill underneath must survive.
    'hover-add-overlap': {
      overlay: null,
      overlayOpacity: 0,
      stroke: 'var(--color-preview-strong)',
      strokeWidth: 2.1,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    // 4/5/6 — one hue, three weights.
    'placed-heavy': {
      overlay: 'var(--color-placed-strong)',
      // Held below ~0.7 so the red/black base still shows through, per §3.1.
      overlayOpacity: 0.68,
      stroke: 'var(--color-placed-strong)',
      strokeWidth: 1.1,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    placed: {
      overlay: 'var(--color-placed)',
      overlayOpacity: 0.5,
      stroke: 'rgba(167,139,250,0.55)',
      strokeWidth: 0.8,
      strokeDash: null,
      labelColour: '#f5f3ff',
    },
    'placed-light': {
      overlay: 'var(--color-placed-deep)',
      overlayOpacity: 0.26,
      stroke: 'rgba(167,139,250,0.30)',
      strokeWidth: 0.7,
      strokeDash: null,
      labelColour: '#ede9fe',
    },
    // 7 — thick white ring, no fill change.
    selected: {
      overlay: null,
      overlayOpacity: 0,
      stroke: '#ffffff',
      strokeWidth: 2.6,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    // 8 — native pocket colour, nothing on top.
    base: {
      overlay: null,
      overlayOpacity: 0,
      stroke: null,
      strokeWidth: 0,
      strokeDash: null,
      labelColour: 'rgba(255,255,255,0.88)',
    },
  },
};

/**
 * Read-only mode's palette (§8): arm's length, bad lighting, one hand.
 *
 * Every treatment is pushed harder — brighter base pockets, thicker strokes,
 * pure white labels — while keeping the two rules §3.1 actually depends on:
 * overlays stay translucent so the red/black base reads underneath, and no two
 * states share both a fill and an outline treatment.
 */
export const CASINO_WHEEL_PALETTE: WheelPalette = {
  ...DEFAULT_WHEEL_PALETTE,
  id: 'casino',
  // Brighter bases: a dim red pocket is unreadable across a table.
  pocketRed: '#d81f2c',
  pocketBlack: '#1c1c24',
  pocketGreen: '#0f9c63',
  separator: 'rgba(255,255,255,0.22)',
  gapArc: 'rgba(220,220,235,0.6)',
  gainBar: '#22e69a',
  lossBar: '#ff4d67',
  baselineRing: 'rgba(255,255,255,0.28)',
  hub: '#000000',
  chipBadge: '#ffffff',
  states: {
    'hover-remove': {
      overlay: '#ffab1a',
      overlayOpacity: 0.7,
      stroke: '#ffffff',
      strokeWidth: 3,
      strokeDash: '4 3',
      labelColour: '#ffffff',
    },
    'hover-add-new': {
      overlay: '#22d3ee',
      overlayOpacity: 0.6,
      stroke: '#a5f3fc',
      strokeWidth: 3,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    'hover-add-overlap': {
      overlay: null,
      overlayOpacity: 0,
      stroke: '#a5f3fc',
      strokeWidth: 3.4,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    'placed-heavy': {
      overlay: '#c4b5fd',
      overlayOpacity: 0.72,
      stroke: '#ede9fe',
      strokeWidth: 2.2,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    placed: {
      overlay: '#8b5cf6',
      overlayOpacity: 0.56,
      stroke: '#c4b5fd',
      strokeWidth: 1.8,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    'placed-light': {
      overlay: '#6d28d9',
      overlayOpacity: 0.34,
      stroke: 'rgba(196,181,253,0.7)',
      strokeWidth: 1.5,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    selected: {
      overlay: null,
      overlayOpacity: 0,
      stroke: '#ffffff',
      strokeWidth: 4,
      strokeDash: null,
      labelColour: '#ffffff',
    },
    base: {
      overlay: null,
      overlayOpacity: 0,
      stroke: null,
      strokeWidth: 0,
      strokeDash: null,
      labelColour: '#ffffff',
    },
  },
};

export const WHEEL_PALETTES: Readonly<Record<WheelPalette['id'], WheelPalette>> = {
  default: DEFAULT_WHEEL_PALETTE,
  casino: CASINO_WHEEL_PALETTE,
};
